import React, { useCallback, useEffect, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import {
  OnboardingHero,
  OnboardingWhatIs,
  OnboardingWorkflowStep,
  OnboardingDemoSection,
  OnboardingNextSection,
  OnboardingCtaButtons,
} from './OnboardingSections';
import type { WelcomeOnboardingContentProps } from './WelcomeOnboardingMobile';
import { useDesignMode } from '../../contexts/DesignContext';
import { SketchButton } from '../sketchy';
import { DESKTOP_ONBOARDING_SLIDE_COUNT, WORKFLOW_STEP_KEYS } from './onboardingConstants';

const GET_STARTED_INDEX = WORKFLOW_STEP_KEYS.length + 1;

export const WelcomeOnboardingDesktop: React.FC<WelcomeOnboardingContentProps & { active?: boolean }> = ({
  trackName,
  trackArtist,
  sharedTabLabel,
  demoLoading,
  demoUnavailable,
  onSkip,
  onDemo,
  onCreate,
  active = true,
}) => {
  const { t } = useTranslation();
  const { isSketchy } = useDesignMode();
  const [slide, setSlide] = useState(0);
  const lastIndex = DESKTOP_ONBOARDING_SLIDE_COUNT - 1;

  const goTo = useCallback(
    (index: number) => {
      setSlide(Math.max(0, Math.min(lastIndex, index)));
    },
    [lastIndex]
  );

  useEffect(() => {
    if (!active) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') goTo(slide - 1);
      if (e.key === 'ArrowRight') goTo(slide + 1);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [active, goTo, slide]);

  return (
    <div className="hidden lg:flex flex-col h-[calc(100vh-4rem)] max-h-[920px] -my-2">
      <div className="flex-1 min-h-0 flex flex-col">
        <div className="flex-1 min-h-0 overflow-hidden relative">
          <div
            className="flex h-full transition-transform duration-300 ease-out"
            style={{ transform: `translateX(-${slide * 100}%)` }}
          >
            {/* Slide 0 — Welcome */}
            <div className="w-full shrink-0 h-full flex items-center justify-center px-4">
              <div className="w-full max-w-5xl grid grid-cols-1 xl:grid-cols-2 gap-8 xl:gap-12 items-center">
                <OnboardingHero onSkip={onSkip} centered variant="slide" />
                <OnboardingWhatIs tilt="right" variant="slide" />
              </div>
            </div>

            {/* Slides 1–5 — One workflow step each */}
            {WORKFLOW_STEP_KEYS.map((stepKey, index) => (
              <div key={stepKey} className="w-full shrink-0 h-full flex items-center justify-center px-4">
                <div className="w-full max-w-3xl">
                  {index === 0 && (
                    <header className="text-center mb-6">
                      <h2 className="text-2xl xl:text-3xl font-semibold scribble-underline inline-block">
                        {t('onboarding.workflow.title')}
                      </h2>
                      <p className="mt-3 text-base xl:text-lg text-[var(--color-muted)] leading-relaxed max-w-2xl mx-auto">
                        {t('onboarding.workflow.intro')}
                      </p>
                    </header>
                  )}
                  <OnboardingWorkflowStep stepKey={stepKey} index={index} variant="slide" />
                </div>
              </div>
            ))}

            {/* Slide 6 — Demo + next steps */}
            <div className="w-full shrink-0 h-full flex items-center justify-center px-4">
              <div className="w-full max-w-3xl space-y-6">
                <OnboardingDemoSection
                  trackName={trackName}
                  trackArtist={trackArtist}
                  sharedTabLabel={sharedTabLabel}
                  demoLoading={demoLoading}
                  demoUnavailable={demoUnavailable}
                  variant="slide"
                />
                <OnboardingNextSection variant="slide" />
              </div>
            </div>
          </div>
        </div>

        {/* Carousel controls */}
        <nav
          className="shrink-0 flex items-center justify-between gap-4 pt-5 pb-2 px-1"
          aria-label={t('onboarding.carousel.label', 'Onboarding slides')}
        >
          <SketchButton
            type="button"
            onClick={() => goTo(slide - 1)}
            disabled={slide === 0}
            className="flex items-center gap-2 px-4 py-2 text-base disabled:opacity-40"
            aria-label={t('onboarding.carousel.previous', 'Previous slide')}
          >
            <ChevronLeft className="w-5 h-5" aria-hidden />
            {t('onboarding.carousel.back', 'Back')}
          </SketchButton>

          <div className="flex items-center gap-2" role="tablist" aria-label={t('onboarding.carousel.dots', 'Slide progress')}>
            {Array.from({ length: DESKTOP_ONBOARDING_SLIDE_COUNT }, (_, i) => (
              <button
                key={i}
                type="button"
                role="tab"
                aria-selected={slide === i}
                aria-label={t('onboarding.carousel.goTo', 'Go to slide {{n}}', { n: i + 1 })}
                onClick={() => goTo(i)}
                className={`rounded-full transition-all ${
                  slide === i
                    ? 'w-8 h-2.5 bg-[var(--brand-from)]'
                    : 'w-2.5 h-2.5 bg-[var(--color-hover)] hover:bg-[var(--color-muted)]'
                }`}
              />
            ))}
          </div>

          {slide < GET_STARTED_INDEX ? (
            <SketchButton
              type="button"
              variant="primary"
              onClick={() => goTo(slide + 1)}
              className="flex items-center gap-2 px-5 py-2 text-base"
              aria-label={t('onboarding.carousel.next', 'Next slide')}
            >
              {t('onboarding.carousel.nextLabel', 'Next')}
              <ChevronRight className="w-5 h-5" aria-hidden />
            </SketchButton>
          ) : (
            <div className="w-[88px]" aria-hidden />
          )}
        </nav>
      </div>

      <footer
        className={`shrink-0 pt-4 pb-2 border-t border-[var(--color-border)] ${isSketchy ? 'border-dashed' : ''}`}
      >
        <OnboardingCtaButtons
          onDemo={onDemo}
          onCreate={onCreate}
          onSkip={onSkip}
          demoLoading={demoLoading}
          demoUnavailable={demoUnavailable}
          layout="row"
          large
        />
      </footer>
    </div>
  );
};
